'use client';

import Confetti from 'react-dom-confetti';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { chunk } from 'lodash-es';
import { toast } from 'react-toastify';
import { MdClose } from 'react-icons/md';
import { useWallet } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';

import { solNetwork } from 'lib/config';
import { usePost, usePut } from 'lib/http';
import {
  lastSuccessfulSubmitState,
  needsRefreshState,
  transactionsToProcessState,
} from 'lib/state';

import LoadingSpinner from 'components/LoadingSpinner';

const ID = 'tp-modal';
const BATCH_SIZE = 5;
const STEP_MESSAGES = {
  preparing: 'Preparing bid transaction; please wait...',
  signing: 'Please sign transaction(s)',
  submitting: 'Submitting your bids; please wait...',
  success: null,
  error: null,
};
const EXPLODE_CONFIG = {
  angle: 90,
  spread: 720,
  startVelocity: 40,
  elementCount: 200,
  duration: 5000,
};

export default function TransactionProcessor() {
  const isDevnet = solNetwork === 'devnet';
  const [numProcessed, setNumProcessed] = useState(0);
  const [step, setStep] = useState('preparing');
  const [txidSummary, setTxidSummary] = useState([]);
  const [errorsSummary, setErrorsSummary] = useState([]);
  const [transactionsToProcess, setTransactionsToProcess] = useRecoilState(
    transactionsToProcessState,
  );
  const [, setLastSuccessfulSubmit] = useRecoilState(lastSuccessfulSubmitState);

  const transactionBatches = useMemo(
    () => chunk(transactionsToProcess, BATCH_SIZE),
    [transactionsToProcess],
  );

  const [, setNeedsRefresh] = useRecoilState(needsRefreshState);

  const { signAllTransactions } = useWallet();
  const httpPost = usePost();
  const httpPut = usePut();

  const clean = useCallback(
    (clearBids = false) => {
      setTransactionsToProcess(null);
      setStep('preparing');
      setNeedsRefresh(new Date().getTime());
      setNumProcessed(0);
      if (clearBids) {
        setLastSuccessfulSubmit(new Date().getTime());
      }
    },
    [setTransactionsToProcess, setNeedsRefresh, setNumProcessed, setStep, setLastSuccessfulSubmit],
  );

  const submit = useCallback(async () => {
    const allTxids = [];
    const allErrors = [];
    const [[[{ project }]]] = transactionBatches;

    for (let i = 0; i < transactionBatches.length; i++) {
      const ts = transactionBatches[i];

      // Generate transactions
      let txes;
      setStep('preparing');
      try {
        const {
          data: { errors, buffers },
        } = await httpPost(`/api/projects/${project}/bid`, { transactionsToProcess: ts });
        if (!buffers?.length) {
          setStep('error');
          setErrorsSummary(errors);
          return;
        }
        txes = buffers.map((b) => Transaction.from(new Uint8Array(b.data)));
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        clean();
        const { message } = err?.response?.data || err;
        toast.error(message || 'Failed to build tx; please refresh and try again');
        return;
      }

      // Sign all transactions
      try {
        setStep('signing');
        txes = await signAllTransactions(txes);
      } catch {
        toast.info('Operation cancelled');
        httpPut(`/api/projects/${project}/bid`, {
          clean: txes.map((tx) => tx.serialize({ requireAllSignatures: false })),
        });
        clean();
        return;
      }

      // Submit transactions for approval
      try {
        const {
          data: { txids, errors },
        } = await httpPut(`/api/projects/${project}/bid`, {
          transactions: txes.map((tx) => tx.serialize()),
        });
        setNumProcessed((n) => n + 1);
        allTxids.push(txids);
        allErrors.push(errors);
      } catch (err) {
        console.error('Failed to submit transactions:', err);
        clean();
        setStep('error');
        const { message } = err?.response?.data || err;
        toast.error(message || 'Error during submission; please confirm on the blockchain');
      }
    }
    setTxidSummary(allTxids.flat());
    setErrorsSummary(allErrors.flat());
    if (allTxids.length > 0) {
      setStep('success');
    } else if (allErrors.length > 0) {
      setStep('error');
    } else {
      clean();
    }
  }, [clean, httpPost, httpPut, signAllTransactions, transactionBatches]);

  useEffect(() => {
    if (transactionsToProcess?.length > 0) {
      submit();
    }
  }, [transactionsToProcess, submit]);

  return (
    <>
      <input
        type="checkbox"
        id={ID}
        className="modal-toggle"
        checked={transactionsToProcess?.length > 0}
        onChange={() => null}
      />
      <div className="modal flex flex-col overflow-hidden h-full justify-center">
        <Confetti active={step === 'success'} config={EXPLODE_CONFIG} />
        <div className="modal-box max-w-lg px-4 md:px-8 pt-0 max-h-[600px]">
          <div className="flex justify-between items-end py-6 sticky top-0 bg-base-100">
            <h3 className="font-bold text-xl">
              {step === 'success'
                ? transactionsToProcess?.length === 1
                  ? 'Transaction submitted  🎉'
                  : 'Transactions submitted  🎉'
                : step === 'error'
                  ? 'Error submitting bids'
                  : 'Submitting Bids...'}
            </h3>
            {['success', 'error'].includes(step) ? (
              <label
                htmlFor={ID}
                className="cursor-pointer stroke-base-content fill-base-content"
                onClick={() => clean(step === 'success')}
              >
                <MdClose size={24} />
              </label>
            ) : (
              <div />
            )}
          </div>
          <div className="flex flex-col items-center space-y-8">
            {STEP_MESSAGES[step] && <div className="font-thin">{STEP_MESSAGES[step]}</div>}
            {step === 'preparing' && <LoadingSpinner color="#1db9d5" size="128px" />}
            {step === 'signing' && (
              <div className="w-full relative pb-10">
                <progress
                  className="progress progress-primary w-full h-8 absolute"
                  value={numProcessed}
                  max={transactionBatches.length}
                ></progress>
                <div className="flex absolute z-10 w-full h-8 items-center justify-center text-neutral-focus">
                  Confirmed {numProcessed} of {transactionBatches.length || 0} batches
                </div>
              </div>
            )}
            {step === 'error' && (
              <div className="flex flex-col w-full font-thin">
                {errorsSummary.length === 0 && (
                  <p>An error was detected; please refresh and try again.</p>
                )}
                {errorsSummary.length > 0 && (
                  <div className="flex flex-col flex-grow overflow-y-auto space-y-4">
                    <ul className="list-disc px-8">
                      {errorsSummary.map((err, i) => (
                        <li key={`error-${i}-${err}`}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {step === 'success' && (
              <div className="flex flex-col w-full">
                {txidSummary.length === 0 && (
                  <p className="text-lg font-light">No bids were succesfully submitted</p>
                )}
                {txidSummary.length > 0 && (
                  <div className="flex flex-col flex-grow overflow-y-auto space-y-2">
                    <div className="flex flex-wrap items-center justify-center">
                      {txidSummary.map((txid, i) => (
                        <div className="tooltip" key={txid} data-tip="View tx on Solscan">
                          <Link
                            className="btn btn-accent btn-outline btn-sm md:btn-md m-2"
                            href={`https://solscan.io/tx/${txid}${
                              isDevnet ? '?cluster=devnet' : ''
                            }`}
                            target="_blank"
                          >
                            <span>Transaction {i + 1}</span>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {errorsSummary.length > 0 && (
                  <div className="flex flex-col flex-grow overflow-y-auto space-y-2 mt-4">
                    <p className="text-lg font-light">Some errors were encountered:</p>
                    <ul className="list-disc px-8">
                      {errorsSummary.map((error, i) => (
                        <li key={`${error}-${i}`}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="modal-action">
              {['success', 'error'].includes(step) && (
                <button className="btn" onClick={() => clean(step === 'success')}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
